<script setup lang="ts">
/**
 * The full story of a change that waits for the creator's OK, on a phone: the recap, the kinds of change, what
 * leaves Lighthouse (highlighted: an email or invoice that goes out, an invite), why it waits, and every change
 * line. Opens from the compact approval card in the chat (medium, drag up for large); Approve / Not now stay in a
 * footer above the home indicator. Things that go out for good are approved with a press and hold.
 */
import { computed } from 'vue'
import DetentSheet from '@/components/mobile/DetentSheet.vue'
import type { ChangesetEffect } from '../api'
import type { ChangeGroup } from '../recap'
import HoldButton from './HoldButton.vue'
import { vPress } from '@/directives/press'

const props = defineProps<{
    open: boolean
    recap: string
    lines: string[]
    groups: ChangeGroup[]
    leaving: Pick<ChangesetEffect, 'summary'>[]
    reasons: string[]
    hold: boolean
    acting: 'approve' | 'reject' | null
    disabled?: boolean
}>()
const emit = defineEmits<{ close: []; approve: []; reject: [] }>()

/** Policy reasons read like "changes more than 10 records (approval needed above 10)": keep the first clause. */
const why = computed(() => props.reasons.map((r) => r.replace(/\s*\(.*\)\s*$/, '').trim()).filter(Boolean))
const busy = computed(() => props.disabled || !!props.acting)
</script>

<template>
    <DetentSheet
        :open="open"
        :detents="['medium', 'large']"
        aria-label="Review changes"
        class="aps"
        data-testid="approval-sheet"
        @close="emit('close')"
    >
        <template #header>
            <div class="aps-head">
                <div class="min-w-0 flex-1">
                    <span class="aps-badge"><span class="aps-dot" aria-hidden="true">!</span>Needs your OK</span>
                    <h2 class="aps-title">Review changes</h2>
                </div>
                <button v-press.icon type="button" class="aps-close" aria-label="Close" @click="emit('close')">
                    <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7" /></svg>
                </button>
            </div>
        </template>

        <div class="aps-body">
            <p class="aps-recap" data-testid="approval-recap">{{ recap }}</p>
            <p v-if="why.length" class="aps-why">
                <svg viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="6" /><path d="M8 7.4v3.4M8 5.2v.1" /></svg>
                <span>Waiting for you because it {{ why.join(' and ') }}.</span>
            </p>

            <ul v-if="groups.length" class="aps-tiles">
                <li v-for="g in groups" :key="`${g.type}:${g.verb}`" class="aps-tile" :class="`verb-${g.verb}`">
                    <span class="aps-tile-icon" aria-hidden="true"><i class="fa-regular" :class="g.icon" /></span>
                    <span>{{ g.label }}</span>
                </li>
            </ul>

            <section v-if="leaving.length" class="aps-leaves" data-testid="approval-leaves">
                <header class="aps-leaves-head">
                    <span class="aps-leaves-icon" aria-hidden="true">
                        <svg viewBox="0 0 16 16"><path d="M6.5 4.5H11.5V9.5M11.5 4.5L4.5 11.5" /></svg>
                    </span>
                    <span class="min-w-0">
                        <span class="aps-leaves-title">Leaves Lighthouse</span>
                        <span class="aps-leaves-sub">{{ hold ? 'Goes out the moment you approve, and can’t be called back.' : 'Happens outside Lighthouse when you approve.' }}</span>
                    </span>
                </header>
                <ul class="aps-leaves-list">
                    <li v-for="(e, i) in leaving" :key="i">{{ e.summary }}</li>
                </ul>
            </section>

            <section v-if="lines.length" class="aps-list">
                <h3 class="aps-list-title">All changes <span>{{ lines.length }}</span></h3>
                <ul class="aps-rows">
                    <li v-for="(l, i) in lines" :key="i" class="aps-row">{{ l }}</li>
                </ul>
            </section>
        </div>

        <template #footer>
            <div class="aps-foot">
                <button
                    v-press.pill
                    type="button"
                    class="aps-btn is-soft"
                    :disabled="busy"
                    data-testid="sheet-reject"
                    @click="emit('reject')"
                >
                    <span v-if="acting === 'reject'" class="aps-spin is-dark" aria-hidden="true" />Not now
                </button>
                <HoldButton v-if="hold" class="aps-btn" :busy="acting === 'approve'" :disabled="disabled || acting === 'reject'" @confirm="emit('approve')" />
                <button
                    v-else
                    v-press.pill
                    type="button"
                    class="aps-btn is-primary"
                    :disabled="busy"
                    data-testid="sheet-approve"
                    @click="emit('approve')"
                >
                    <span v-if="acting === 'approve'" class="aps-spin" aria-hidden="true" />Approve
                </button>
            </div>
        </template>
    </DetentSheet>
</template>

<style scoped>
.aps-head {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 2px 16px 12px 20px;
}
.aps-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #e2400f;
}
html.dark .aps-badge { color: #ff8a5c; }
.aps-dot {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    background: var(--agent-accent);
}
.aps-title {
    margin-top: 2px;
    font-size: 20px;
    font-weight: 650;
    letter-spacing: -0.01em;
    color: var(--agent-text-1);
}
.aps-close {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    margin-top: 2px;
    border-radius: 999px;
    color: var(--agent-text-2);
    background: var(--agent-hover);
}
.aps-close svg { width: 14px; height: 14px; fill: none; stroke: currentColor; stroke-width: 1.8; stroke-linecap: round; }

.aps-body { display: flex; flex-direction: column; gap: 16px; padding: 0 20px 20px; }
.aps-recap { font-size: 17px; line-height: 1.5; color: var(--agent-text-1); text-wrap: pretty; }
.aps-why {
    display: flex;
    gap: 8px;
    margin-top: -6px;
    font-size: 13.5px;
    line-height: 1.45;
    color: var(--agent-text-3);
}
.aps-why svg { flex-shrink: 0; width: 15px; height: 15px; margin-top: 2px; fill: none; stroke: currentColor; stroke-width: 1.4; stroke-linecap: round; }

.aps-tiles { display: flex; flex-wrap: wrap; gap: 6px; margin: 0; padding: 0; list-style: none; }
.aps-tile {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 34px;
    padding: 4px 12px 4px 4px;
    border-radius: 999px;
    font-size: 13.5px;
    color: var(--agent-text-2);
    background: var(--agent-hover);
}
.aps-tile-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 999px;
    font-size: 11.5px;
    color: var(--agent-text-2);
    background: var(--agent-card);
    box-shadow: 0 1px 2px rgb(15 15 15 / 0.08);
}
.verb-create .aps-tile-icon, .verb-rates .aps-tile-icon { color: #059669; }
.verb-move .aps-tile-icon { color: #2563eb; }
.verb-status .aps-tile-icon { color: #d97706; }
.verb-dates .aps-tile-icon { color: #7c3aed; }
.verb-remove .aps-tile-icon { color: #dc2626; }
.verb-send .aps-tile-icon { color: #e2400f; }

.aps-leaves {
    padding: 14px 16px;
    border-radius: 16px;
    background: rgb(251 76 31 / 0.07);
    box-shadow: inset 0 0 0 1px rgb(251 76 31 / 0.2);
}
html.dark .aps-leaves { background: rgb(251 76 31 / 0.12); box-shadow: inset 0 0 0 1px rgb(251 76 31 / 0.3); }
.aps-leaves-head { display: flex; align-items: flex-start; gap: 10px; }
.aps-leaves-icon {
    display: grid;
    place-items: center;
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 999px;
    background: var(--agent-accent);
}
.aps-leaves-icon svg { width: 14px; height: 14px; fill: none; stroke: #fff; stroke-width: 1.8; stroke-linecap: round; stroke-linejoin: round; }
.aps-leaves-title { display: block; font-size: 15px; font-weight: 600; color: var(--agent-text-1); }
.aps-leaves-sub { display: block; margin-top: 1px; font-size: 13px; line-height: 1.4; color: var(--agent-text-3); }
.aps-leaves-list { display: flex; flex-direction: column; gap: 6px; margin: 12px 0 0 38px; padding: 0; list-style: none; }
.aps-leaves-list li { font-size: 14.5px; line-height: 1.45; font-weight: 500; color: var(--agent-text-1); overflow-wrap: anywhere; }

.aps-list-title {
    display: flex;
    align-items: baseline;
    gap: 6px;
    margin: 0 0 8px 4px;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: var(--agent-text-3);
}
.aps-list-title span { font-weight: 500; color: var(--agent-text-4); }
.aps-rows {
    margin: 0;
    padding: 0;
    list-style: none;
    overflow: hidden;
    border-radius: 14px;
    background: var(--agent-hover);
}
.aps-row {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 44px;
    padding: 10px 16px 10px 30px;
    font-size: 14.5px;
    line-height: 1.4;
    color: var(--agent-text-1);
    overflow-wrap: anywhere;
}
.aps-row::before {
    content: '';
    position: absolute;
    left: 15px;
    top: 50%;
    width: 5px;
    height: 5px;
    margin-top: -2.5px;
    border-radius: 999px;
    background: var(--agent-text-4);
}
.aps-row + .aps-row { box-shadow: inset 0 1px 0 var(--agent-card-border); }

.aps-foot {
    display: flex;
    gap: 10px;
    padding: 10px 16px 10px;
    border-top: 1px solid var(--agent-card-border);
}
.aps-btn {
    display: inline-flex;
    flex: 1 1 0;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 50px;
    border-radius: 14px;
    font-size: 16px;
    font-weight: 600;
    -webkit-tap-highlight-color: transparent;
}
.aps-btn:disabled { opacity: 0.5; }
.aps-btn.is-soft { color: var(--agent-text-1); background: var(--agent-press); }
.aps-btn.is-primary {
    color: #fff;
    background: linear-gradient(180deg, var(--agent-accent-2) 0%, var(--agent-accent) 100%);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.35), 0 6px 16px -6px rgb(251 76 31 / 0.6);
}
.aps-spin {
    width: 15px;
    height: 15px;
    border-radius: 999px;
    border: 2px solid rgb(255 255 255 / 0.4);
    border-top-color: #fff;
    animation: aps-spin 0.7s linear infinite;
}
.aps-spin.is-dark { border-color: rgb(15 15 15 / 0.15); border-top-color: var(--agent-text-2); }
@keyframes aps-spin { to { transform: rotate(360deg); } }
</style>
